import axios from 'axios';
import Cookies from 'js-cookie/src/js.cookie';

// Helper to safely get current cart items
const getCurrentCartItems = () => {
  if (window.blubolt && window.blubolt.cart && window.blubolt.cart.currentCart && window.blubolt.cart.currentCart.items) {
    return window.blubolt.cart.currentCart.items;
  }
  return [];
};

// Checks if Gift product is in cart based on current snapshot
const isGiftProductInCart = (giftProductObject, currentCartItems) => {
  return currentCartItems.some(cartItem => cartItem.variant_id === giftProductObject.id);
}

// Function to perform cart updates (removals)
const updateCartItems = async (updates) => {
  if (Object.keys(updates).length === 0) {
    return false;
  }
  try {
    await axios.post('/cart/update.js', { updates });
    return true; // Indicates a change was made
  } catch (error) {
    console.error('Error updating cart items:', error);
    return false;
  }
};

// Function to add items to cart
const addCartItems = async (items) => {
  if (items.length === 0) {
    return false;
  }
  try {
    await axios.post('/cart/add.js', { items });
    return true; // Indicates a change was made
  } catch (error) {
    console.error('Error adding gift items:', error);
    return false;
  }
};

// Handle explicit gift removal by the user (e.g., clicking a remove button)
const setupGiftRemoveListener = () => {
  document.querySelectorAll('[data-remove]').forEach(button => {
    button.addEventListener('click', async (event) => {
      const dataGiftVar = Number(event.currentTarget.getAttribute('data-gift-variant'));
      if (!isNaN(dataGiftVar)) {
        Cookies.set('data-gift-variant', dataGiftVar, { expires: 1 }); // Set cookie for 1 day
        const updates = { [dataGiftVar]: 0 };
        const removed = await updateCartItems(updates);
        if (removed) {
          window.location.reload();
        }
      }
    });
  });
};


export const addGiftToCart = async () => {

  const checkCart = async () => {
    console.log('--- checkCart started ---');

    // Basic checks for necessary global variables
    if (!window.freeCartGifts || window.freeCartGifts.length <= 0) {
      console.log('DEBUG: No freeCartGifts defined or empty.');
      return;
    }
    if (!window.cartProductTags || window.cartProductTags.length <= 0) {
      console.log('DEBUG: No cartProductTags defined or empty.');
      return;
    }
    // If a gift was explicitly removed by user, respect that for a short period
    if (Cookies.get('data-gift-variant')) {
      console.log('DEBUG: User explicitly removed a gift recently. Skipping auto-add.');
      return;
    }

    let cartChanged = false; // Flag to track if any cart modification happened

    // Removed the subscription filtering logic as it's no longer needed.
    let availableFreeGifts = [...window.freeCartGifts]; // Use a copy of all defined gifts
    console.log('DEBUG: All available free gifts:', availableFreeGifts);


    const currentCartItems = getCurrentCartItems(); // Get current cart snapshot
    console.log('DEBUG: Current cart items (from blubolt.cart):', currentCartItems);

    // Calculate cart total from non-gift items (for general minSpend checks)
    let cartTotal = 0;
    window.cartProductTags.forEach(cartItem => {
      // Only sum up non-gift items for the cart total for min spend calculation
      if (!availableFreeGifts.some(gift => gift.id === cartItem.variant_id)) {
        cartTotal += cartItem.price * cartItem.qty;
      }
    });
    console.log('DEBUG: Calculated cart total (excluding current gifts):', cartTotal);

    const giftsToKeepInCart = new Set(); // IDs of gifts that should be in cart
    const itemsToAdd = []; // Gifts eligible AND not in cart
    const updatesForRemoval = {}; // { variant_id: 0 } for gifts to remove

    // --- New Logic: Determine Dominant Tag Group ---
    const tagValuesInCart = {}; // Stores total value for each relevant tag in the cart

    // Collect all unique tags from free gifts that we care about
    const allGiftTags = new Set(availableFreeGifts.map(gift => gift.tag).filter(tag => tag)); // Filter out empty/null tags
    console.log('DEBUG: All unique gift-related tags:', [...allGiftTags]);

    // Calculate the total value of products for each relevant tag in the cart
    window.cartProductTags.forEach(cartItem => {
      // Only consider non-gift items
      if (!availableFreeGifts.some(gift => gift.id === cartItem.variant_id)) {
        cartItem.tags.forEach(itemTag => {
          if (allGiftTags.has(itemTag)) { // Only consider tags that are relevant to free gifts
            tagValuesInCart[itemTag] = (tagValuesInCart[itemTag] || 0) + (cartItem.price * cartItem.qty);
          }
        });
      }
    });
    console.log('DEBUG: Tag values in cart:', tagValuesInCart);

    let dominantTag = null;
    let maxTagValue = 0;

    // Find the tag with the highest value
    // Handle the case where no tags are present or all have 0 value
    const tagsWithValues = Object.keys(tagValuesInCart).filter(tag => tagValuesInCart[tag] > 0);
    if (tagsWithValues.length > 0) {
        // Sort tags by value descending, pick the first
        tagsWithValues.sort((a, b) => tagValuesInCart[b] - tagValuesInCart[a]);
        dominantTag = tagsWithValues[0];
        maxTagValue = tagValuesInCart[dominantTag];
    }

    console.log('DEBUG: Dominant Tag:', dominantTag, 'with value:', maxTagValue);

    // --- Determine eligibility for gifts based on dominant tag and minSpend ---
    for (const giftProductObject of availableFreeGifts) {
      let giftCriteriaMet = false;

      const isDominantTagGift = dominantTag && giftProductObject.tag === dominantTag;
      const generalMinSpendMet = cartTotal >= giftProductObject.minSpend; // Use for gifts with NO specific tag
      const tagSpecificMinSpendMet = isDominantTagGift && maxTagValue >= giftProductObject.minSpend; // For gifts matching dominant tag

      console.log(`DEBUG: Processing gift: ${giftProductObject.title} (ID: ${giftProductObject.id}, Tag: "${giftProductObject.tag}", MinSpend: ${giftProductObject.minSpend})`);
      console.log(`  DEBUG: isDominantTagGift: ${isDominantTagGift} (Gift tag: "${giftProductObject.tag}", Dominant tag: "${dominantTag}")`);
      console.log(`  DEBUG: generalMinSpendMet (cartTotal ${cartTotal} >= ${giftProductObject.minSpend}): ${generalMinSpendMet}`);
      console.log(`  DEBUG: tagSpecificMinSpendMet (dominantTag value ${maxTagValue} >= ${giftProductObject.minSpend}): ${tagSpecificMinSpendMet}`);


      if (isDominantTagGift && tagSpecificMinSpendMet) {
          // This gift's tag is the dominant tag AND its specific tag value meets its minSpend
          giftCriteriaMet = true;
          console.log('  DEBUG: -> Criteria met: Dominant tag and tag-specific minSpend met.');
      } else if (giftProductObject.tag === '' && generalMinSpendMet) {
          // This gift has NO specific tag (empty string) AND general cart total meets its minSpend
          // This handles "any cart" type gifts.
          giftCriteriaMet = true;
          console.log('  DEBUG: -> Criteria met: No specific tag and general minSpend met.');
      } else {
          console.log('  DEBUG: -> Criteria NOT met for this gift.');
      }

      if (giftCriteriaMet) {
        giftsToKeepInCart.add(giftProductObject.id); // Mark as eligible
        if (!isGiftProductInCart(giftProductObject, currentCartItems)) {
          itemsToAdd.push({ id: giftProductObject.id, quantity: 1 });
          console.log(`  DEBUG: -> Added to itemsToAdd: ${giftProductObject.title}`);
        } else {
            console.log(`  DEBUG: -> Gift already in cart: ${giftProductObject.title}`);
        }
      }
    }

    console.log('DEBUG: Gifts to keep in cart (IDs):', [...giftsToKeepInCart]);
    console.log('DEBUG: Items to add (newly eligible):', itemsToAdd);


    // --- Determine gifts to remove (ineligible or no longer eligible) ---
    currentCartItems.forEach(cartItem => {
      const isAGift = availableFreeGifts.some(gift => gift.id === cartItem.variant_id);
      if (isAGift && !giftsToKeepInCart.has(cartItem.variant_id)) {
        // This gift is in cart but shouldn't be
        updatesForRemoval[cartItem.variant_id] = 0;
        console.log(`DEBUG: Marking for removal: ${cartItem.product_title} (ID: ${cartItem.variant_id})`);
      }
    });
    console.log('DEBUG: Updates for removal:', updatesForRemoval);


    // --- Execute Cart Modifications (Batching) ---

    // 1. Perform removals first
    if (Object.keys(updatesForRemoval).length > 0) {
      console.log('DEBUG: Executing removals...');
      const removed = await updateCartItems(updatesForRemoval);
      if (removed) cartChanged = true;
      console.log('DEBUG: Removals completed. Cart changed:', removed);
    }

    // 2. Then perform additions (only if removals didn't trigger a reload)
    // If a reload happens after removals, the add logic will run again on fresh data.
    if (itemsToAdd.length > 0) {
      console.log('DEBUG: Executing additions...');
      const added = await addCartItems(itemsToAdd);
      if (added) cartChanged = true;
      console.log('DEBUG: Additions completed. Cart changed:', added);
    }

    // Only reload if an actual change was made
    if (cartChanged) {
      console.log('DEBUG: Cart changed. Reloading page...');
      window.location.reload();
    } else {
        console.log('DEBUG: No cart changes detected. No reload.');
    }
    console.log('--- checkCart finished ---');
  };

  // Initial setup for user-initiated gift removal (only run once)
  setupGiftRemoveListener();

  // Polling mechanism to wait for cart data
  const checkExist = setInterval(function() {
    // Ensure both blubolt.cart and its currentCart property are available
    if (window.blubolt && window.blubolt.cart && window.blubolt.cart.currentCart) {
      clearInterval(checkExist);
      checkCart();
    }
  }, 200);
}